/* Where the cursor is in the grid: the active cell, the corner a range was started from, and
   the range between them, as a sheet keeps them. Moving clamps to the grid; Shift extends the
   range; a drag over cells extends it too. Kept in refs as well as state, so a key handler
   reading them mid-gesture never sees a stale render. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface Pos {
  r: number;
  c: number;
}

export interface Range {
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

export function useGridSelection(rowCount: number, columnCount: number) {
  const [active, setActiveState] = useState<Pos | null>(null);
  const [anchor, setAnchorState] = useState<Pos | null>(null);
  const [dragging, setDragging] = useState(false);
  const current = useRef<{ active: Pos | null; anchor: Pos | null }>({ active: null, anchor: null });

  const setActive = useCallback((pos: Pos | null) => {
    current.current.active = pos;
    setActiveState(pos);
  }, []);
  const setAnchor = useCallback((pos: Pos | null) => {
    current.current.anchor = pos;
    setAnchorState(pos);
  }, []);

  /** The cursor at `pos`, clamped; `extend` keeps the range's corner where it was. */
  const moveTo = useCallback(
    (pos: Pos, extend = false) => {
      const clamped = {
        r: Math.max(0, Math.min(rowCount - 1, pos.r)),
        c: Math.max(0, Math.min(columnCount - 1, pos.c)),
      };
      if (extend) {
        if (!current.current.anchor) setAnchor(current.current.active ?? clamped);
      } else {
        setAnchor(null);
      }
      setActive(clamped);
    },
    [rowCount, columnCount, setActive, setAnchor],
  );

  const selectAll = useCallback(() => {
    setAnchor({ r: 0, c: 0 });
    setActive({ r: rowCount - 1, c: columnCount - 1 });
  }, [rowCount, columnCount, setActive, setAnchor]);


  const clear = useCallback(() => {
    setActive(null);
    setAnchor(null);
  }, [setActive, setAnchor]);

  // The grid shrank under the cursor (a column hidden, a shorter page): it stays inside, and the
  // range below is clamped in the same render, before this puts the state right.
  useEffect(() => {
    if (!rowCount || !columnCount) {
      if (current.current.active) clear();
      return;
    }
    const fit = (pos: Pos | null) => pos && { r: Math.min(pos.r, rowCount - 1), c: Math.min(pos.c, columnCount - 1) };
    const { active: was, anchor: from } = current.current;
    if (was && (was.r >= rowCount || was.c >= columnCount)) setActive(fit(was));
    if (from && (from.r >= rowCount || from.c >= columnCount)) setAnchor(fit(from));
  }, [rowCount, columnCount, clear, setActive, setAnchor]);

  const range: Range | null = useMemo(() => {
    if (!active || !rowCount || !columnCount) return null;
    const corner = anchor ?? active;
    return {
      r1: Math.min(corner.r, active.r, rowCount - 1),
      r2: Math.min(Math.max(corner.r, active.r), rowCount - 1),
      c1: Math.min(corner.c, active.c, columnCount - 1),
      c2: Math.min(Math.max(corner.c, active.c), columnCount - 1),
    };
  }, [active, anchor, rowCount, columnCount]);

  const inRange = useCallback(
    (r: number, c: number) => !!range && r >= range.r1 && r <= range.r2 && c >= range.c1 && c <= range.c2,
    [range],
  );

  // A drag ends wherever the pointer is let go, on the grid or not.
  useEffect(() => {
    if (!dragging) return;
    const stop = () => setDragging(false);
    window.addEventListener("pointerup", stop);
    return () => window.removeEventListener("pointerup", stop);
  }, [dragging]);

  /** The pointer passing over a cell while dragging: the range grows to it. */
  const dragOver = useCallback(
    (pos: Pos) => {
      if (!dragging) return;
      if (!current.current.anchor) setAnchor(current.current.active);
      setActive(pos);
    },
    [dragging, setActive, setAnchor],
  );

  return { active, range, inRange, moveTo, selectAll, clear, dragging, setDragging, dragOver, current };
}
